/**
 * MyAgreements — the client's list of TREC agreements + signing history.
 *
 * Reached at `/agreements`. Lists every form the client has (buyer
 * representation agreements, disclosures, …) with its status, and — depending
 * on state — a link to sign (SENT_FOR_SIGNATURE) or download the executed PDF
 * (SIGNED). Backs `GET /trec/forms` (client-role callers see only their own).
 *
 * Previously a client could only reach a form via the one-off `/trec/sign/{id}`
 * deep link in an email; there was no place to see what they'd signed.
 */

import { Download, PenLine } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";

import type { FormOut, FormStatus } from "@re-platform/api-client";
import { formService } from "@re-platform/api-client";
import { Button, Skeleton, StatusPill, type StatusTone } from "@re-platform/ui";

import { api } from "../../lib/api";
import { loadSession } from "../auth/session";

import "./SignDocument.css";

const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
  window.location.origin;

function statusTone(status: FormStatus): StatusTone {
  if (status === "SIGNED") return "success";
  if (status === "SENT_FOR_SIGNATURE") return "info";
  return "neutral";
}

function statusLabel(status: FormStatus): string {
  switch (status) {
    case "DRAFT":
    case "PREFILLED":
      return "Being prepared";
    case "SENT_FOR_SIGNATURE":
      return "Ready to sign";
    case "SIGNED":
      return "Signed";
    default:
      return status;
  }
}

/**
 * Client-facing names for the forms.
 *
 * Sentence case and spelled out, unlike the staff console's Title Case
 * abbreviations: a client reads "Information about brokerage services", not
 * "IABS". The old fallback just un-underscored the key, which rendered the
 * disclosure as "Sellers disclosure" and the IABS as, literally, "Iabs".
 *
 * The fallback stays for a form type this build has not heard of — better a
 * readable guess than a raw key — but every type the server can send is named
 * here.
 */
const TYPE_LABELS: Record<string, string> = {
  buyer_representation: "Buyer representation agreement",
  buyer_representation_agreement: "Buyer representation agreement",
  sellers_disclosure: "Seller's disclosure notice",
  iabs: "Information about brokerage services",
  exclusive_right_to_sell: "Listing agreement",
  exclusive_right_to_lease: "Listing agreement (lease)",
  consumer_protection_notice: "Consumer protection notice",
  dual_capacity_consent: "Dual capacity disclosure",
  residential_contract_1_to_4_family: "Residential contract (1–4 family)",
  home_inspection_notice: "Home inspection notice",
  lease_application: "Lease application",
  tenant_application: "Tenant application",
  addendum: "Addendum",
  amendment: "Amendment",
};

function typeLabel(t: string): string {
  const known = TYPE_LABELS[t];
  if (known) return known;
  const s = t.replace(/_/g, " ").trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * The line under the title: where this document stands, in the client's terms.
 *
 * A form still being prepared has no date worth showing — "created Jul 14" is
 * our bookkeeping, not their business — so it says who has it instead.
 */
function subLine(form: FormOut): string {
  if (form.status === "DRAFT" || form.status === "PREFILLED")
    return "Being prepared by your agent";

  const when =
    form.status === "SIGNED"
      ? `signed ${formatDate(form.updated_at)}`
      : `sent ${formatDate(form.created_at)}`;

  // A seller's disclosure is ABOUT a house, and the type name alone does not
  // say which — a seller with two listings could not tell their own documents
  // apart. Lead with the address when the form has one.
  const line = form.property_address
    ? `${form.property_address} · ${when}`
    : when;
  return line.charAt(0).toUpperCase() + line.slice(1);
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
}

/** One application's documents, in the order the packet was built.
 *
 * A client with a lease listing, a sale and a purchase has three packets and
 * around a dozen rows, and the same document type appears in all three — "IABS"
 * three times, "Consumer protection notice" three times. Flat, there is nothing
 * on a row that says which application it answers, so the list is unreadable
 * exactly when it matters most. Reported from real use at Checkpoint #19.
 *
 * Grouped on `dossier_id`: one packet is one dossier's documents (V089), which
 * is precisely the boundary the client thinks in.
 */
interface FormGroup {
  key: string;
  heading: string | null;
  forms: FormOut[];
}

function groupByApplication(forms: readonly FormOut[]): FormGroup[] {
  const groups = new Map<string, FormGroup>();
  for (const form of forms) {
    // Packets predating V089 have no dossier — they group under one unlabelled
    // heading rather than each pretending to be its own application.
    const key = form.dossier_id ?? "unlinked";
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        heading:
          form.application_number != null
            ? `Application #${form.application_number}`
            : null,
        forms: [],
      };
      groups.set(key, group);
    }
    // The address is per-application, not per-row: once it is in the heading,
    // repeating it on every card is noise.
    if (group.heading && form.property_address)
      group.heading = `Application #${form.application_number} · ${form.property_address}`;
    group.forms.push(form);
  }
  return [...groups.values()];
}

export function MyAgreements() {
  const q = useQuery({
    queryKey: ["my-agreements"],
    queryFn: () => formService.list(api),
  });

  // Once per render: the count and the rendered list must be the same grouping,
  // not two calls that could disagree.
  const groups = useMemo(() => groupByApplication(q.data ?? []), [q.data]);

  return (
    <div className="trec-page">
      <header className="trec-head">
        <h1>My agreements</h1>
        <p>
          Your representation agreements and disclosures — sign what's pending
          and download anything you've already signed.
        </p>
      </header>

      {q.isPending ? (
        <Skeleton variant="block" style={{ height: 220 }} />
      ) : q.isError ? (
        <div className="trec-card" role="status">
          We couldn't load your agreements right now. Please try again shortly.
        </div>
      ) : (q.data ?? []).length === 0 ? (
        <div className="trec-card" role="status">
          <strong>Nothing here yet</strong>
          <p style={{ marginBottom: 0 }}>
            When your agent sends you a document to sign, it'll show up here.
          </p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
          {groups.map((group) => (
            <ApplicationGroup
              key={group.key}
              group={group}
              // A single application needs no heading telling the client so, and
              // nothing to collapse: the whole page IS that application.
              collapsible={groups.length > 1}
              // Anything still awaiting a signature stays open — the reason to
              // open this page at all is the thing that needs signing, and
              // hiding it behind a closed group would bury the one actionable
              // item. Everything else starts closed.
              defaultOpen={group.forms.some(isActionable)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** A form the client still has to do something about. */
function isActionable(form: FormOut): boolean {
  return form.status !== "SIGNED" && form.status !== "VOIDED";
}

/**
 * One application's documents, collapsible.
 *
 * A client with three applications had ~12 rows on one page with "IABS" in all
 * three and no way to see one application at a time. Grouping alone (#141) told
 * them WHICH application a row belonged to; folding is what lets them look at
 * one without reading past the others.
 *
 * A native <details> rather than useState: it is keyboard-accessible and
 * screen-reader-announced for free, and the browser's own disclosure semantics
 * are what a group of documents actually is.
 */
function ApplicationGroup({
  group,
  collapsible,
  defaultOpen,
}: {
  group: FormGroup;
  collapsible: boolean;
  defaultOpen: boolean;
}) {
  const rows = (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {group.forms.map((form) => (
        <AgreementRow key={form.id} form={form} />
      ))}
    </div>
  );

  if (!collapsible || !group.heading) return <section>{rows}</section>;

  const pending = group.forms.filter(isActionable).length;
  return (
    <details className="trec-group" open={defaultOpen}>
      <summary className="trec-group-summary">
        <h2 className="trec-group-heading">{group.heading}</h2>
        <span className="trec-group-count">
          {pending > 0
            ? `${pending} to sign · ${group.forms.length} document${group.forms.length === 1 ? "" : "s"}`
            : `${group.forms.length} document${group.forms.length === 1 ? "" : "s"}`}
        </span>
      </summary>
      {rows}
    </details>
  );
}

function AgreementRow({ form }: { form: FormOut }) {
  return (
    <div className="trec-card trec-agreement">
      <div className="trec-agreement__main">
        <div className="trec-card__title">{typeLabel(form.form_type)}</div>
        <p className="trec-agreement__sub">{subLine(form)}</p>
      </div>
      {/* Status and the thing to do about it, together on the right: the pill
          answers "where is this", the button answers "and now what".
          Splitting them put those at opposite ends of the card. */}
      <div className="trec-agreement__actions">
        <StatusPill tone={statusTone(form.status)}>
          {statusLabel(form.status)}
        </StatusPill>
        <AgreementAction form={form} />
      </div>
    </div>
  );
}

function AgreementAction({ form }: { form: FormOut }) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function download() {
    setError(null);
    setDownloading(true);
    try {
      const token = loadSession()?.accessToken;
      // NO `credentials:"include"` — auth is the Bearer token above, and the
      // API answers with a wildcard Access-Control-Allow-Origin so that every
      // per-tenant custom domain (V026) works without being allowlisted. The
      // spec rejects a wildcard origin whenever credentials mode is "include",
      // so asking for credentials made the browser discard the response and
      // never send the GET at all — the download simply failed with the
      // preflight succeeding. Same reasoning as packages/api-client/src/client.ts.
      const resp = await fetch(`${API_BASE}/trec/forms/${form.id}/signed-pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error(`Download failed (${resp.status})`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `trec-${form.form_type}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Couldn't download the document.",
      );
    } finally {
      setDownloading(false);
    }
  }

  if (form.status === "SIGNED") {
    return (
      <div style={{ textAlign: "right" }}>
        <Button
          variant="secondary"
          size="sm"
          onClick={download}
          disabled={downloading}
        >
          <Download size={15} aria-hidden />{" "}
          {downloading ? "Preparing…" : "Download PDF"}
        </Button>
        {error && (
          <p
            style={{
              color: "var(--rp-color-danger, #dc2626)",
              fontSize: 12,
              margin: "6px 0 0",
            }}
          >
            {error}
          </p>
        )}
      </div>
    );
  }
  if (form.status === "SENT_FOR_SIGNATURE") {
    return (
      <Link to={`/trec/sign/${form.id}`}>
        <Button size="sm">
          <PenLine size={15} aria-hidden /> Review &amp; sign
        </Button>
      </Link>
    );
  }
  return null;
}
